import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { StackNavigation } from './StackNavigation';
import { createChildren } from '../Box/createChildren';
import { getContainerStyles } from '../Box/getContainerStyles';
import { StackContainerState, } from '../../store/birdElement/stackContainerState';
import { setConstraints } from '../../store/birdLayout/BirdLayoutState';
import { HorizontalPosition, VerticalPosition, ButtonType } from '@sas-dvr/ltjs-commons';
import { selectBirdStackContainerState, selectLayoutState, selectBirdElementsState, selectAccessibilityLabelState, } from '../../store/selectors';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { exposeTestId } from '../../config';
import { DragAndDropProvider } from '@sas/nova-commons/dnd/DragAndDropProvider';
function getFlexDirection(direction, hp, vp) {
    if (direction === 'horizontal') {
        return vp === VerticalPosition.BOTTOM ? 'column-reverse' : 'column';
    }
    return hp === HorizontalPosition.RIGHT ? 'row-reverse' : 'row';
}
export const StackContainerBase = ({ id, state, layoutState, elementState, accessibilityLabel, actions, enableDrag, }) => {
    var _a, _b;
    const { buttonType = ButtonType.BUTTON, direction = 'horizontal', horizontalPosition = HorizontalPosition.CENTER, verticalPosition = VerticalPosition.MIDDLE, selectedIndex = 0, labels = [], } = state;
    const [focusedIndex, setFocusedIndex] = useState(selectedIndex);
    const stackNavRef = useRef(null);
    useEffect(() => {
        if (!stackNavRef.current) {
            return;
        }
        const isVertical = direction === 'vertical';
        const newWidth = isVertical ? stackNavRef.current.clientWidth : 0;
        const newHeight = isVertical ? 0 : stackNavRef.current.clientHeight;
        const constraints = layoutState.constraints;
        if (!constraints ||
            constraints.additionalContentWidth !== newWidth ||
            constraints.additionalContentHeight !== newHeight) {
            actions.setConstraints(id, {
                additionalContentWidth: newWidth,
                additionalContentHeight: newHeight,
            });
        }
    });
    const childItems = createChildren(layoutState.childKeys, elementState);
    const children = childItems
        ? childItems.map((value, index) => {
            const selected = index === selectedIndex;
            // This div is only created for the display property. the only reason why all of the children need to be
            //   rendered is so their constraints can be considered in the layout. However, if we were able to ensure the
            //   constraints are set without rendering we could only render the active child.
            return (_jsx("div", Object.assign({ "data-testid": exposeTestId() ? id + '-stack-layer-' + index : undefined, style: { display: selected ? 'block' : 'none' } }, { children: value }), index));
        })
        : null;
    const flexDirection = getFlexDirection(direction, horizontalPosition, verticalPosition);
    const onChange = useCallback((selectedIndex) => {
        setFocusedIndex(selectedIndex);
        actions.update(id, { selectedIndex });
    }, [actions, id]);
    const onReorderItems = useCallback((oldItemIndex, newItemIndex) => {
        actions.notifyReorderItems(id, { oldItemIndex, newItemIndex });
    }, [actions, id]);
    let navigation = (_jsx(StackNavigation, { id: id + '-stack-navigation', ref: stackNavRef, items: labels, onChange: onChange, onReorderItems: onReorderItems, onFocusedIndexChange: setFocusedIndex, focusedIndex: focusedIndex, buttonType: buttonType, direction: direction, horizontalPosition: horizontalPosition, verticalPosition: verticalPosition, selectedIndex: selectedIndex, enableDrag: (_a = state.enableDrag) !== null && _a !== void 0 ? _a : enableDrag }));
    if ((_b = state.enableDrag) !== null && _b !== void 0 ? _b : enableDrag) {
        navigation = _jsx(DragAndDropProvider, Object.assign({ enableDragDrop: true }, { children: navigation }));
    }
    return (_jsxs("div", Object.assign({ style: { display: 'flex', flexDirection: flexDirection, flexGrow: 1, height: 0 }, "aria-label": accessibilityLabel }, { children: [navigation, _jsx("div", Object.assign({ style: Object.assign(Object.assign({}, getContainerStyles(layoutState)), { flexGrow: 1 }) }, { children: children }))] })));
};
export const StackContainer = connect((state, props) => ({
    state: selectBirdStackContainerState(state, props.id),
    // The stack container needs to re-render every time its LayoutData['position']
    //  Changes so that the additionalContentWidth and Height can be updated to reflect
    //  the StackNavigation's new size since the navigation component has a width of
    //  25% when the direction is left or right.
    layoutState: selectLayoutState(state, props.id),
    elementState: selectBirdElementsState(state),
    accessibilityLabel: selectAccessibilityLabelState(state, props.id),
    enableDrag: state.sections.tabsEditable,
}), (dispatch) => ({
    actions: bindActionCreators(Object.assign({ setConstraints }, StackContainerState.actions), dispatch),
}))(StackContainerBase);
